import { Button } from '@src/components/Button';
import { Visibility } from '@src/features/Users/components/Visibility';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { motion } from 'framer-motion';
import { isEmpty, isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { WithCard } from '../WithCard';

/**
 * Widget
 *
 * The logic for the Widget component.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function TheWidget(props) {
  const {
    backgroundColor,
    button,
    children,
    collapsed,
    collapsible,
    description,
    icon,
    isFullscreen,
    menu,
    setIsFullscreen,
    title,
    withCard,
  } = props;

  const [isCollapsed, setIsCollapsed] = useState(collapsed || false);

  return (
    <motion.div
      animate="open"
      initial={{ opacity: 0 }}
      variants={{
        closed: { opacity: 0 },
        open: { opacity: 1 },
      }}
    >
      <div className="widget">
        <WithCard backgroundColor={backgroundColor} withCard={withCard}>
          <Flex
            alignItems={isEmpty(description) ? 'center' : 'flex-start'}
            className="widget-header"
            justify="space-between"
          >
            <Layout.Group alignItems="top">
              {!isEmpty(icon) && (
                <div>
                  <IconSymbol color="inherit" icon={icon} variant="outlined" />
                </div>
              )}
              <Layout.Stack alignItems="left" space={0}>
                <Heading offset={5}>{title}</Heading>
                {!isEmpty(description) && (
                  <div>
                    <Text>{description}</Text>
                  </div>
                )}
              </Layout.Stack>
            </Layout.Group>

            <Layout.Group className="widget-options">
              {!isUndefined(button) && button}
              {!isEmpty(menu) && (
                <div>
                  <DropdownMenu
                    after={<IconSymbol color="$gray7" icon="more_vert" variant="outlined" />}
                    css={{
                      '.abyss-dropdown-menu-trigger': {
                        backgroundColor: 'none',
                        margin: 0,
                        padding: 0,
                      },
                    }}
                    hideLabel
                    label="options"
                    menuItems={menu || []}
                  />
                </div>
              )}
              {collapsible === true && (
                <Button
                  onClick={() => {
                    return setIsCollapsed(!isCollapsed);
                  }}
                  rounded
                  size="$sm"
                  style={{
                    border: '1px solid var(--abyss-colors-interactive1) !important',
                    borderRadius: '4px !important',
                    margin: 0,
                  }}
                  variant="ghost"
                >
                  {isCollapsed ? (
                    <IconSymbol icon="expand_less" variant="outlined" />
                  ) : (
                    <IconSymbol icon="expand_more" variant="outlined" />
                  )}
                </Button>
              )}
              <Visibility
                accessor="FullScreenWidgets"
                disabledEnvironments={['Local', 'Development', 'Stage', 'Production']}
                enabledEnvironments={[]}
              >
                <div>
                  <button type="button">
                    <IconSymbol
                      css={{
                        backgroundColor: 'none',
                        cursor: 'pointer',
                        margin: 0,
                        padding: 'var(--abyss-space-sm)',
                      }}
                      icon="fullscreen"
                      onClick={() => {
                        return setIsFullscreen(!isFullscreen);
                      }}
                    />
                  </button>
                </div>
              </Visibility>
            </Layout.Group>
          </Flex>
          {!isCollapsed && (
            <React.Fragment>
              <Divider className="widget-divider" />
              <div className="widget-body">
                <motion.div
                  animate="open"
                  initial={{ opacity: 0 }}
                  variants={{
                    closed: { opacity: 0 },
                    open: { opacity: 1 },
                  }}
                >
                  {children}
                </motion.div>
              </div>
            </React.Fragment>
          )}
        </WithCard>
      </div>
    </motion.div>
  );
}

TheWidget.propTypes = {
  backgroundColor: PropTypes.string,
  button: PropTypes.node,
  children: PropTypes.node,
  collapsed: PropTypes.bool,
  collapsible: PropTypes.bool,
  description: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isFullscreen: PropTypes.bool,
  menu: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onChange: PropTypes.func,
      radios: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      title: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  setIsFullscreen: PropTypes.func,
  title: PropTypes.string.isRequired,
  withCard: PropTypes.bool,
};

TheWidget.defaultProps = {
  backgroundColor: '',
  button: null,
  children: null,
  collapsed: false,
  collapsible: false,
  description: '',
  icon: '',
  isFullscreen: false,
  menu: [],
  setIsFullscreen: () => {},
  withCard: true,
};
